import { z } from 'zod';
import { AuditSchema, IdSchema } from './common.ts';
import { MoneySchema, addMoney, money, subtractMoney, type Money } from '../money.ts';
import { lineBuilderCost, lineOwnerPrice, type CostLine } from './cost-line.ts';

/**
 * One row of a job's budget, per cost code.
 *
 * **The document id is the cost code id**, which is what makes approving an
 * estimate safe to run twice: the same approval writes the same rows rather
 * than a second set. `docs/DATA_MODEL.md` §6 has it that way and the reason
 * is worth stating, because a ULID id here would look more consistent with
 * the rest of the product and would be wrong.
 *
 * **Two columns are stored and the rest are derived.** `original` and
 * `complete` are the only things a person sets — the first by approving an
 * estimate (or by typing it, for a job with no estimate), the second by
 * ticking a box. `committed`, `actual`, `projected` and the variances are
 * sums over purchase orders, bills and receipts, and live in `jobCosting`
 * (P5-06) rather than here. A stored `actual` is a number that disagrees
 * with the bills it came from, and the disagreement is found during an
 * argument about an invoice.
 *
 * `revised` is **not a field here at all**, which is the easiest one to get
 * wrong: it reads like something the approval should freeze, but it is
 * `original` plus the approved change orders and the selection deltas, and
 * every one of those can be voided or re-answered after the fact. A stored
 * `revised` would be a second opinion about a sum the change orders already
 * hold, and the two would part company the first time one was voided.
 */
export const BudgetLineSchema = AuditSchema.extend({
  /** The cost code id. Also the document id — see above. */
  id: IdSchema,
  companyId: IdSchema,
  jobId: IdSchema,
  costCodeId: IdSchema,
  /** What the approved estimate said this code would cost. */
  original: MoneySchema,
  /** What the owner was originally going to be charged for it. */
  ownerOriginal: MoneySchema,
  /**
   * Ticked when there is no more to spend against this code.
   *
   * Changes what `projected` means: a complete code projects its actual
   * rather than the greater of its revised and committed, which is how a
   * job that came in under budget stops looking like one that has not
   * finished spending.
   */
  complete: z.boolean().default(false),
  completedAt: z.string().datetime().optional(),
  /**
   * Which estimate produced this row.
   *
   * Provenance, so a budget page can say "from Estimate 3" and a question
   * about a number has somewhere to go. Absent on a row somebody typed.
   */
  sourceEstimateId: IdSchema.optional(),
});
export type BudgetLine = z.infer<typeof BudgetLineSchema>;

/**
 * The budget rows an estimate's lines imply.
 *
 * One row per cost code, summing the lines costed against it. Lines with
 * **no** cost code are deliberately left out rather than collected into an
 * "Uncoded" row: a budget row's id is its cost code id, and there is no id
 * for "none". `uncodedLines` reports them separately so the approval can
 * refuse, which is the right answer — approving an estimate with uncoded
 * money would create a contract whose total nobody can reconcile against a
 * budget.
 */
export interface BudgetSeed {
  readonly rows: readonly {
    readonly costCodeId: string;
    readonly original: Money;
    readonly ownerOriginal: Money;
  }[];
  readonly uncodedLines: readonly CostLine[];
}

export function budgetSeedFrom(lines: readonly CostLine[], currency = 'USD'): BudgetSeed {
  const byCode = new Map<string, { original: Money; ownerOriginal: Money }>();
  const uncodedLines: CostLine[] = [];

  for (const line of lines) {
    if (!line.costCodeId) {
      uncodedLines.push(line);
      continue;
    }
    const running = byCode.get(line.costCodeId) ?? {
      original: money(0, currency),
      ownerOriginal: money(0, currency),
    };
    byCode.set(line.costCodeId, {
      original: addMoney(running.original, lineBuilderCost(line)),
      ownerOriginal: addMoney(running.ownerOriginal, lineOwnerPrice(line)),
    });
  }

  return {
    rows: [...byCode.entries()].map(([costCodeId, totals]) => ({ costCodeId, ...totals })),
    uncodedLines,
  };
}

/** What a budget row is over or under, before anything has been spent. */
export function budgetMargin(line: Pick<BudgetLine, 'original' | 'ownerOriginal'>): Money {
  return subtractMoney(line.ownerOriginal, line.original);
}

/** A whole budget's original figures. */
export function budgetTotals(
  lines: readonly BudgetLine[],
  currency = 'USD',
): { readonly original: Money; readonly ownerOriginal: Money; readonly margin: Money } {
  const original = lines.reduce((sum, line) => addMoney(sum, line.original), money(0, currency));
  const ownerOriginal = lines.reduce(
    (sum, line) => addMoney(sum, line.ownerOriginal),
    money(0, currency),
  );
  return { original, ownerOriginal, margin: subtractMoney(ownerOriginal, original) };
}
